import { FC, useState, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Button, Field, UnknownFunction } from '@eon-home/react-library';

import { WithLoader } from '@hocs/with-loader';

import {
    composeClassName,
    hasErrors,
    useHasEvSmartChargingC2CReadScope,
    useHasGridXAutopilotChargingReadScope,
} from '@tools/utils';
import {
    useElectricCarConfigId,
    useElectricCarVendor,
    useIsAddressSet,
    useSite,
} from '@store/selectors';
import {
    setAddress,
    setSuccessToast,
    EmobilityAddressDataProps,
    setAddressBeforeLinking,
    updateSiteAddress,
} from '@store/actions';
import { GoogleAPILocationRequestModel } from '@swagger-http';

import './index.scss';
import { ElectricCarVendors } from '@store/enums';
import { ENODE_STATE } from '@tools/constants';

type Props = {
    onSuccess?: UnknownFunction;
    onCancel?: UnknownFunction;
    shouldSetAddressBeforeLinking?: boolean;
    cancelText?: string;
    isReCollectAddressPrompt?: boolean;
    inline: boolean; // inline address forms do not have headers & display buttons on the right
};

export const ElectricCarAddressForm: FC<Readonly<Props>> = ({
    onCancel,
    onSuccess,
    cancelText,
    shouldSetAddressBeforeLinking = false,
    isReCollectAddressPrompt = false,
    inline,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: 'onSubmit',
    });

    const hasEvSmartChargingC2CReadScope = useHasEvSmartChargingC2CReadScope();
    const hasGridXAutopilotChargingReadScope =
        useHasGridXAutopilotChargingReadScope();
    const configId = useElectricCarConfigId();
    const vendor = useElectricCarVendor();
    const isAddressSet = useIsAddressSet();
    const site = useSite();

    const [isLoading, setIsLoading] = useState(false);

    const [hasError, setHasError] = useState(false);

    const showCancelButton: boolean = useMemo(
        () => typeof onCancel === 'function' && !inline,
        [inline, onCancel],
    );

    const getFormHeader = useCallback(() => {
        if (isReCollectAddressPrompt) {
            return t('Enable Smart Charging and Get Your Bonus');
        }

        return t('EV smart charging at home');
    }, [isReCollectAddressPrompt, t]);

    const getFormDesc = useCallback(() => {
        if (isReCollectAddressPrompt) {
            return (
                <>
                    <p>
                        {t(
                            'Smart Charging only works when your vehicle is at home.',
                        )}
                    </p>
                    <p>
                        {t(
                            "For quality assurance purposes, we therefore ask you to re-enter your wallbox's location information to ensure that charging processes run properly.",
                        )}
                    </p>
                </>
            );
        }
        if (hasEvSmartChargingC2CReadScope)
            return (
                <p>
                    {t(
                        'Please enter the wallbox location information. We check before each charging session whether the vehicle is connected to the specified wallbox and will only offer smart charging once the vehicle is at home.',
                    )}
                </p>
            );
        if (hasGridXAutopilotChargingReadScope)
            return (
                <p>
                    {t(
                        'Please enter the wallbox location information. We check before each charging session whether the vehicle is connected to the specified wallbox and will only offer flexible charging once the vehicle is at home.',
                    )}
                </p>
            );

        return (
            <p>
                {t(
                    'To smart charge your vehicle at home, we need your home charging location.',
                )}
            </p>
        );
    }, [
        hasEvSmartChargingC2CReadScope,
        hasGridXAutopilotChargingReadScope,
        isReCollectAddressPrompt,
        t,
    ]);

    const setAddressAction = useCallback(
        async (addressDataProps: EmobilityAddressDataProps) => {
            if (isReCollectAddressPrompt) {
                const response = await dispatch(
                    updateSiteAddress(site.id, addressDataProps.address),
                ).catch(() => false);

                return response;
            }

            if (shouldSetAddressBeforeLinking) {
                const response = await setAddressBeforeLinking(
                    addressDataProps.address,
                );

                if (typeof response !== 'boolean') {
                    localStorage.setItem(ENODE_STATE, response?.state ?? '');
                }

                return response;
            }

            const response = await dispatch(
                setAddress(
                    addressDataProps,
                    vendor === ElectricCarVendors.MERCEDES,
                ),
            ).catch((e) => e);

            return response;
        },
        [
            dispatch,
            isReCollectAddressPrompt,
            shouldSetAddressBeforeLinking,
            site.id,
            vendor,
        ],
    );

    const onSubmit = useCallback(
        async ({
            street,
            houseNumber,
            city,
            zipCode,
        }: {
            street: string;
            houseNumber: string;
            city: string;
            zipCode: string;
        }): Promise<void> => {
            setIsLoading(true);
            const addressDataProps: EmobilityAddressDataProps = {
                configId,
                address: {
                    street,
                    houseNumber,
                    city,
                    zipCode,
                } as GoogleAPILocationRequestModel,
                isAddressSet: Boolean(isAddressSet),
            };

            const response = await setAddressAction(addressDataProps);

            setIsLoading(false);

            if (typeof response === 'boolean' && !response) {
                setHasError(true);
                return;
            }

            dispatch(setSuccessToast());

            typeof onSuccess === 'function' &&
                onSuccess(shouldSetAddressBeforeLinking ? response : {});
        },
        [
            configId,
            isAddressSet,
            setAddressAction,
            dispatch,
            onSuccess,
            shouldSetAddressBeforeLinking,
        ],
    );

    return (
        <div className="c-address-form">
            <form
                className={composeClassName('c-address-form', [
                    inline ? '' : 'compact',
                ])}
                onSubmit={handleSubmit(onSubmit)}
            >
                {!inline && (
                    <header className="c-address-form__head">
                        <h3>{getFormHeader()}</h3>
                        {getFormDesc()}
                    </header>
                )}

                <div className="c-address-form__body">
                    <Field
                        name="street"
                        label={t('Street name *')}
                        type="text"
                        error={errors.street}
                        register={register('street', {
                            required: t('This field is required.'),
                        })}
                    />
                    <Field
                        name="houseNumber"
                        label={t('House number *')}
                        type="text"
                        error={errors.houseNumber}
                        register={register('houseNumber', {
                            required: t('This field is required.'),
                        })}
                    />
                    <Field
                        name="city"
                        label={t('Town or City *')}
                        type="text"
                        error={errors.city}
                        register={register('city', {
                            required: t('This field is required.'),
                        })}
                    />
                    <Field
                        name="zipCode"
                        label={t('Post Code *')}
                        type="text"
                        error={errors.zipCode}
                        register={register('zipCode', {
                            required: t('This field is required.'),
                        })}
                    />

                    {hasError && (
                        <p className="is--error">
                            {t(
                                'The home location you entered is incorrect. Please try again.',
                            )}
                        </p>
                    )}
                </div>

                <footer
                    className={`c-address-form__actions ${composeClassName(
                        'c-address-form__actions',
                        [inline ? '' : 'center'],
                    )}`}
                >
                    {showCancelButton && (
                        <Button type="button" outline onClick={onCancel}>
                            {cancelText ?? t('Cancel')}
                        </Button>
                    )}

                    <WithLoader
                        loading={isLoading}
                        className="c-loader"
                        loaderProps={{ small: true }}
                    >
                        <Button type="submit" disabled={hasErrors(errors)}>
                            {inline ? t('Save') : t('Save and Continue')}
                        </Button>
                    </WithLoader>
                </footer>

                <p className={inline ? 'c-address-form__hint' : ''}>
                    {t('Fields marked with an asterisk (*) are mandatory.')}
                </p>
            </form>
        </div>
    );
};

export default ElectricCarAddressForm;
